import { useEffect } from 'react';

const SEO = ({ 
  title = 'Sentora Holidays - Experience Creators Since 1985 | Premium India Travel',
  description = 'Discover unforgettable travel experiences across India with Sentora Holidays. 40 years of crafting complete journeys with handpicked hotels, expert guides, and seamless transport.',
  type = 'website',
  image = 'https://www.sentoraholidays.com/logo.png',
  url = '',
  keywords = '',
  schema = null
}) => {
  useEffect(() => {
    // Update document title
    document.title = title;
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', description);
    
    // Update OG tags
    const updateOrCreateMeta = (property, content, isProperty = true) => {
      const selector = isProperty ? `meta[property="${property}"]` : `meta[name="${property}"]`;
      let meta = document.querySelector(selector);
      if (!meta) {
        meta = document.createElement('meta');
        if (isProperty) {
          meta.setAttribute('property', property);
        } else {
          meta.setAttribute('name', property);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Keywords meta tag
    if (keywords) {
      updateOrCreateMeta('keywords', keywords, false);
    }

    // Open Graph tags
    updateOrCreateMeta('og:title', title);
    updateOrCreateMeta('og:description', description);
    updateOrCreateMeta('og:type', type);
    updateOrCreateMeta('og:image', image);
    if (url) updateOrCreateMeta('og:url', url);
    
    // Twitter Card tags
    updateOrCreateMeta('twitter:card', 'summary_large_image', false);
    updateOrCreateMeta('twitter:title', title, false);
    updateOrCreateMeta('twitter:description', description, false);
    updateOrCreateMeta('twitter:image', image, false);

    // Schema.org JSON-LD
    const existingSchema = document.querySelector('script[data-schema="sentora"]');
    if (existingSchema) {
      existingSchema.remove();
    }

    const schemaData = schema || {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "TravelAgency",
          "@id": "https://www.sentoraholidays.com/#organization",
          "name": "Sentora Holidays",
          "alternateName": "Sam Tours and Travels",
          "url": "https://www.sentoraholidays.com",
          "logo": "https://www.sentoraholidays.com/logo.png",
          "description": "Experience creators since 1985. 40 years of crafting unforgettable travel experiences across India with handpicked hotels, expert guides, and seamless transport.",
          "foundingDate": "1985",
          "sameAs": [
            "https://facebook.com/sentoraholidays",
            "https://instagram.com/sentoraholidays",
            "https://twitter.com/sentoraholidays"
          ],
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "IN"
          },
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+91-96267-70072",
            "email": "tours@sentoraholidays.com",
            "contactType": "customer service",
            "availableLanguage": ["English", "Hindi"]
          },
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.8",
            "reviewCount": "5000",
            "bestRating": "5",
            "worstRating": "1"
          }
        },
        {
          "@type": "WebSite",
          "@id": "https://www.sentoraholidays.com/#website",
          "url": "https://www.sentoraholidays.com",
          "name": "Sentora Holidays - Experience Creators Since 1985",
          "description": description,
          "publisher": {
            "@id": "https://www.sentoraholidays.com/#organization"
          },
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://www.sentoraholidays.com/packages?search={search_term_string}",
            "query-input": "required name=search_term_string"
          }
        },
        {
          "@type": "WebPage",
          "@id": url || "https://www.sentoraholidays.com/#webpage",
          "url": url || "https://www.sentoraholidays.com",
          "name": title,
          "description": description,
          "isPartOf": {
            "@id": "https://www.sentoraholidays.com/#website"
          },
          "about": {
            "@id": "https://www.sentoraholidays.com/#organization"
          }
        }
      ]
    };

    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.setAttribute('data-schema', 'sentora');
    schemaScript.textContent = JSON.stringify(schemaData);
    document.head.appendChild(schemaScript);

    return () => {
      const schemaToRemove = document.querySelector('script[data-schema="sentora"]');
      if (schemaToRemove) schemaToRemove.remove();
    };
  }, [title, description, type, image, url, keywords, schema]);

  return null;
};

export default SEO;
